import { createFileRoute, Link } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { z } from 'zod';
import {
  SeccionAvisos,
  SeccionCatalogo,
  SeccionEvidencia,
  SeccionFases,
  SeccionTaller,
} from '@/admin/config/Secciones';
import { SeccionAvisosLegales } from '@/admin/config/SeccionAvisosLegales';
import { SeccionExportar } from '@/admin/config/SeccionExportar';
import { SeccionFotos } from '@/admin/config/SeccionFotos';
import { SeccionMarca } from '@/admin/config/SeccionMarca';
import { useConfig, useTaller } from '@/datos/consultas';
import { exigir } from '@/lib/guardias';
import { permisos, useSesionActiva, useTallerId } from '@/lib/sesion';
import { Encabezado } from '@/shell/Encabezado';
import { ErrorCarga, Esqueleto } from '@/ui/basicos';
import { cn } from '@/ui/cn';
import { Contenedor } from '@/ui/pagina';

const secciones = [
  { id: 'taller', texto: 'Datos del taller' },
  { id: 'marca', texto: 'Marca' },
  { id: 'fases', texto: 'Cupos y tiempos' },
  { id: 'catalogo', texto: 'Catálogo' },
  { id: 'avisos', texto: 'Avisos' },
  { id: 'legales', texto: 'Avisos legales' },
  { id: 'fotos', texto: 'Fotos' },
  { id: 'evidencia', texto: 'Evidencia' },
  { id: 'exportar', texto: 'Exportar datos' },
] as const;
type IdSeccion = (typeof secciones)[number]['id'];

export const Route = createFileRoute('/_app/admin/configuracion')({
  beforeLoad: exigir(permisos.administrar),
  validateSearch: z.object({
    seccion: z
      .enum(secciones.map((s) => s.id) as [IdSeccion, ...IdSeccion[]])
      .optional()
      .catch(undefined),
  }),
  component: Pagina,
});

function Pagina() {
  const { seccion = 'taller' } = Route.useSearch();
  const config = useConfig();
  const taller = useTaller();
  const c = config.data;
  const sesion = useSesionActiva();
  const tallerId = useTallerId();
  const nav = useRef<HTMLElement>(null);
  // Mobile: keep the active pill visible in the horizontal strip.
  useEffect(() => {
    nav.current
      ?.querySelector<HTMLElement>('[aria-current="page"]')
      ?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [seccion]);
  return (
    <>
      <Encabezado
        titulo="Configuración"
        ayuda="configuracion"
        subtitulo={taller.data?.nombre ?? undefined}
        buscador={false}
      />
      <Contenedor ancho="angosto" className="lg:max-w-[1120px] lg:flex-row lg:items-start lg:gap-7">
        <nav
          ref={nav}
          aria-label="Secciones de configuración"
          className="sin-scrollbar -mx-4 flex flex-none gap-1.5 overflow-x-auto px-4 lg:sticky lg:top-24 lg:mx-0 lg:w-[200px] lg:flex-col lg:px-0"
        >
          {secciones.map((s) => (
            <Link
              key={s.id}
              to="/admin/configuracion"
              search={{ seccion: s.id === 'taller' ? undefined : s.id }}
              replace
              aria-current={seccion === s.id ? 'page' : undefined}
              className={cn(
                'flex h-[38px] flex-none items-center rounded-full border px-3.5 text-[14px] whitespace-nowrap transition-colors lg:h-10 lg:rounded-[10px] lg:border-0',
                seccion === s.id
                  ? 'border-primary bg-primary font-bold text-on-primary lg:bg-primary-soft lg:text-primary-text'
                  : 'border-border-input text-text-long hover:bg-surface-muted',
              )}
            >
              {s.texto}
            </Link>
          ))}
        </nav>
        <div className="min-w-0 flex-1">
          {config.isError ? (
            <ErrorCarga error={config.error} />
          ) : !c ? (
            <Esqueleto className="h-96 rounded-tarjeta" />
          ) : seccion === 'taller' ? (
            <SeccionTaller config={c} />
          ) : seccion === 'marca' ? (
            <SeccionMarca config={c} />
          ) : seccion === 'fases' ? (
            <SeccionFases config={c} />
          ) : seccion === 'catalogo' ? (
            <SeccionCatalogo config={c} />
          ) : seccion === 'avisos' ? (
            <SeccionAvisos config={c} taller={taller.data?.nombre ?? 'el taller'} />
          ) : seccion === 'legales' ? (
            <SeccionAvisosLegales config={c} />
          ) : seccion === 'fotos' ? (
            <SeccionFotos config={c} tallerId={tallerId} rol={sesion.rol} />
          ) : seccion === 'exportar' ? (
            <SeccionExportar />
          ) : (
            <SeccionEvidencia config={c} />
          )}
        </div>
      </Contenedor>
    </>
  );
}
